import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import Sidebar from './Sidebar';
import Messages from './Messages';

function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    publications: 0,
    scholarships: 0,
    congresses: 0,
    activeTasks: 0,
    completedTasks: 0,
    overdueTasks: 0
  });
  const [recentTasks, setRecentTasks] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [newTasksCount, setNewTasksCount] = useState(0);
  const [notificationsViewed, setNotificationsViewed] = useState(false);

  const loadDashboardData = async () => {
    try {
      const requests = [
        api.getPublications(),
        api.getScholarships(),
        api.getCongresses(),
        api.getMeetings()
      ];

      // Members also need to fetch deleted tasks
      if (user.role === 'member') {
        requests.push(api.getDeletedTasks());
      }

      const responses = await Promise.all(requests);
      const [pubRes, scholRes, congRes, meetingsRes, deletedTasksRes] = responses;
      
      // Process tasks
      let allTasks = [];
      let activities = [];
      
      meetingsRes.data.forEach(meeting => {
        meeting.tasks.forEach(task => {
          const taskData = {
            ...task,
            meetingId: meeting._id,
            meetingTitle: meeting.title,
            meetingDescription: meeting.description
          };
          
          // Filter based on user role
          if (user.role === 'member') {
            if (task.assigned_to?._id === user.id) {
              allTasks.push(taskData);
            }
          } else {
            allTasks.push(taskData);
          }

          // Collect recent activities
          if (task.modification_history && task.modification_history.length > 0) {
            task.modification_history.forEach(history => {
              // Only show activities relevant to the user
              if (user.role === 'member') {
                // Members see their own activities or activities on their tasks
                if (history.modified_by?._id === user.id || task.assigned_to?._id === user.id) {
                  activities.push({
                    ...history,
                    taskId: task._id,
                    meetingId: meeting._id,
                    taskDescription: task.task_description,
                    meetingTitle: meeting.title,
                    assignedTo: task.assigned_to
                  });
                }
              } else if (user.role === 'director') {
                // Directors see all activities in their lab
                activities.push({
                  ...history,
                  taskId: task._id,
                  meetingId: meeting._id,
                  taskDescription: task.task_description,
                  meetingTitle: meeting.title,
                  assignedTo: task.assigned_to
                });
              }
            });
          }
        });
      });

      // Add deleted tasks to activities for members
      if (user.role === 'member' && deletedTasksRes) {
        deletedTasksRes.data.forEach(deletedTask => {
          activities.push({
            taskId: deletedTask._id,
            meetingId: deletedTask.meeting_id,
            taskDescription: deletedTask.task_description,
            meetingTitle: deletedTask.meeting_title,
            assignedTo: deletedTask.assigned_to,
            modified_by: deletedTask.deleted_by,
            modified_at: deletedTask.deleted_at,
            action: 'deleted',
            isDeleted: true,
            deletedTaskId: deletedTask._id,
            is_viewed_deletion: deletedTask.is_viewed_deletion
          });
        });
      }

      // Sort activities by date (most recent first)
      activities.sort((a, b) => new Date(b.modified_at) - new Date(a.modified_at));
      
      // Calculate task statistics
      const activeTasks = allTasks.filter(t => t.status !== 'completed');
      const completedTasks = allTasks.filter(t => t.status === 'completed');
      const overdueTasks = activeTasks.filter(t => t.due_date && new Date(t.due_date) < new Date());
      
      // Get recent tasks (last 5 active tasks)
      const recent = activeTasks
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);

      // Count new tasks for members (include unviewed deleted tasks)
      let newCount = 0;
      if (user.role === 'member') {
        newCount = activeTasks.filter(t => !t.is_viewed).length;
        if (deletedTasksRes) {
          newCount += deletedTasksRes.data.filter(dt => !dt.is_viewed_deletion).length;
        }
      } else {
        newCount = activeTasks.filter(t => t.status === 'pending').length;
      }

      setStats({
        publications: pubRes.data.length,
        scholarships: scholRes.data.length,
        congresses: congRes.data.length,
        activeTasks: activeTasks.length,
        completedTasks: completedTasks.length,
        overdueTasks: overdueTasks.length
      });

      setRecentTasks(recent);
      setRecentActivity(activities.slice(0, 10)); // Top 10 recent activities
      setNewTasksCount(newCount);
      
      // Reset viewed flag if there are new activities
      if (activities.length > 0) {
        setNotificationsViewed(false);
      }
    } catch (err) {
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      return diffInMinutes < 1 ? 'Just now' : `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`;
    }
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const getActionIcon = (action) => {
    switch(action) {
      case 'created': return '➕';
      case 'status_changed': return '🔄';
      case 'task_updated': return '✏️';
      case 'completed': return '✅';
      case 'deleted': return '🗑️';
      default: return '📝';
    }
  };

  const getActionColor = (action) => {
    switch(action) {
      case 'created': return '#4CAF50';
      case 'status_changed': return '#2196F3';
      case 'task_updated': return '#FF9800';
      case 'completed': return '#9C27B0';
      case 'deleted': return '#EF4444';
      default: return '#757575';
    }
  };

  const handleNotificationClick = (activity) => {
    // Remove this notification from the list (mark as read)
    setRecentActivity(prev => prev.filter((item, idx) => 
      !(item.taskId === activity.taskId && item.modified_at === activity.modified_at)
    ));
    
    // If it's a deleted task, navigate differently
    if (activity.isDeleted) {
      navigate('/tasks', { 
        state: { 
          openDeletedTaskId: activity.deletedTaskId
        } 
      });
    } else {
      // Navigate to tasks page with task and meeting ID in state
      navigate('/tasks', { 
        state: { 
          openTaskId: activity.taskId,
          openMeetingId: activity.meetingId
        } 
      });
    }
    setShowNotifications(false);
  };

  return (
    <div className="dashboard-container">
      <Sidebar user={user} onLogout={onLogout} active="dashboard" newTasksCount={newTasksCount} />
      
      <div className="main-content">
        <div className="page-header">
          <div>
            <h1>Dashboard</h1>
            <p>Welcome back, {user.full_name || user.username}!</p>
          </div>
          
          {/* Notification Icons */}
          <div className="notification-icons-container">
            {/* Messages Icon (Blue) - Chat/Discussion */}
            <div className="notification-bell-container">
              <button 
                className="notification-bell-btn messages-btn"
                onClick={() => setShowMessages(!showMessages)}
                title="Messages"
              >
                <svg className="icon-svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
                </svg>
                {/* Placeholder for message count */}
                {/* eslint-disable-next-line no-self-compare */}
                {0 > 0 && (
                  <span className="notification-count messages-count">0</span>
                )}
              </button>

              {/* Messages Dropdown */}
              {showMessages && (
                <Messages 
                  user={user} 
                  showMessages={showMessages} 
                  setShowMessages={setShowMessages} 
                />
              )}
            </div>

            {/* Notifications Bell (Green) */}
            <div className="notification-bell-container">
              <button 
                className="notification-bell-btn notifications-btn"
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  // Mark as viewed when opening
                  if (!showNotifications && recentActivity.length > 0) {
                    setNotificationsViewed(true);
                  }
                }}
                title="Notifications"
              >
                <svg className="icon-svg bell-svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
                </svg>
                {recentActivity.length > 0 && !notificationsViewed && (
                  <span className="notification-count notifications-count">{recentActivity.length}</span>
                )}
              </button>

              {showNotifications && (
              <div className="notifications-dropdown">
                <div className="notifications-header">
                  <h3>Recent Activity</h3>
                  <button 
                    className="close-notifications"
                    onClick={() => setShowNotifications(false)}
                  >
                    ✕
                  </button>
                </div>
                <div className="notifications-list">
                  {recentActivity.length > 0 ? (
                    recentActivity.map((activity, index) => (
                      <div 
                        key={index} 
                        className="notification-item"
                        onClick={() => handleNotificationClick(activity)}
                      >
                        <div 
                          className="notification-icon"
                          style={{ backgroundColor: getActionColor(activity.action) }}
                        >
                          {getActionIcon(activity.action)}
                        </div>
                        <div className="notification-content">
                          <p className="notification-text">
                            <strong>{activity.modified_by?.full_name || activity.modified_by?.username || 'Someone'}</strong>
                            {' '}
                            {activity.action === 'created' && 'created'}
                            {activity.action === 'status_changed' && 'changed status of'}
                            {activity.action === 'task_updated' && 'updated'}
                            {activity.action === 'completed' && 'completed'}
                            {activity.action === 'deleted' && 'deleted'}
                            {' '}
                            {user.role === 'director' && activity.assignedTo && activity.action !== 'deleted' && (
                              <>task for <strong>{activity.assignedTo.full_name || activity.assignedTo.username}</strong></>
                            )}
                            {user.role === 'member' && activity.action !== 'deleted' && activity.modified_by?._id !== user.id && (
                              <>your task</>
                            )}
                            {activity.action === 'deleted' && user.role === 'member' && (
                              <>your task</>
                            )}
                          </p>
                          <p className="notification-task">{activity.taskDescription}</p>
                          <span className="notification-time">{formatDateTime(activity.modified_at)}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-notifications">
                      <p>No recent activity</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            gap: '20px'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              border: '4px solid #f3f4f6',
              borderTop: '4px solid #7c5dfa',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{
              fontSize: '1.1rem',
              color: '#6b7280',
              fontWeight: '500',
              margin: 0
            }}>Loading Dashboard...</p>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        ) : (
          <>
            {/* Statistics Grid */}
            <div className="stats-grid-enhanced">
              <div className="stat-card-enhanced stat-primary">
                <div className="stat-icon">
                  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 10L16 12V52L18 54H46L48 52V22L40 14H18Z" fill="white"/>
                    <path d="M40 14V20L42 22H48L40 14Z" fill="rgba(255,255,255,0.7)"/>
                    <rect x="24" y="28" width="16" height="2.5" rx="1.25" fill="#8B7AFF"/>
                    <rect x="24" y="34" width="16" height="2.5" rx="1.25" fill="#9B8AFF"/>
                    <rect x="24" y="40" width="12" height="2.5" rx="1.25" fill="#AB9AFF"/>
                    <path d="M18 10L16 12V52L18 54H46L48 52V22L40 14H18Z" fill="none" stroke="rgba(124,93,250,0.3)" strokeWidth="2"/>
                  </svg>
                </div>
                <div className="stat-info">
                  <h3>{stats.publications}</h3>
                  <p>Publications</p>
                </div>
              </div>
              
              <div className="stat-card-enhanced stat-success">
                <div className="stat-icon">
                  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M32 14L12 24V30L32 40L52 30V24L32 14Z" fill="white" opacity="0.95"/>
                    <path d="M12 34V40L32 50L52 40V34L32 44L12 34Z" fill="white" opacity="0.85"/>
                    <path d="M12 44V50L32 60L52 50V44L32 54L12 44Z" fill="white" opacity="0.75"/>
                    <circle cx="32" cy="20" r="2.5" fill="#4CAF50"/>
                    <path d="M32 14L12 24L32 34L52 24L32 14Z" fill="none" stroke="#4CAF50" strokeWidth="1.5"/>
                  </svg>
                </div>
                <div className="stat-info">
                  <h3>{stats.scholarships}</h3>
                  <p>Scholarships</p>
                </div>
              </div>
              
              <div className="stat-card-enhanced stat-info">
                <div className="stat-icon">
                  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="12" y="26" width="40" height="28" rx="2" fill="white"/>
                    <rect x="16" y="30" width="5" height="5" rx="1" fill="#00BCD4"/>
                    <rect x="23" y="30" width="5" height="5" rx="1" fill="#00BCD4"/>
                    <rect x="30" y="30" width="5" height="5" rx="1" fill="#00BCD4"/>
                    <rect x="37" y="30" width="5" height="5" rx="1" fill="#00BCD4"/>
                    <rect x="16" y="37" width="5" height="5" rx="1" fill="#26C6DA"/>
                    <rect x="23" y="37" width="5" height="5" rx="1" fill="#26C6DA"/>
                    <rect x="30" y="37" width="5" height="5" rx="1" fill="#26C6DA"/>
                    <rect x="37" y="37" width="5" height="5" rx="1" fill="#26C6DA"/>
                    <rect x="16" y="44" width="5" height="5" rx="1" fill="#4DD0E1"/>
                    <rect x="23" y="44" width="5" height="5" rx="1" fill="#4DD0E1"/>
                    <rect x="30" y="44" width="5" height="5" rx="1" fill="#4DD0E1"/>
                    <rect x="37" y="44" width="5" height="5" rx="1" fill="#4DD0E1"/>
                    <path d="M22 26L32 10L42 26H22Z" fill="white"/>
                    <path d="M22 26L32 12L42 26" stroke="#00BCD4" strokeWidth="2" fill="none"/>
                  </svg>
                </div>
                <div className="stat-info">
                  <h3>{stats.congresses}</h3>
                  <p>Congresses</p>
                </div>
              </div>

              <div className="stat-card-enhanced stat-warning">
                <div className="stat-icon">
                  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="16" y="12" width="32" height="42" rx="3" fill="white"/>
                    <rect x="24" y="8" width="16" height="8" rx="2" fill="white" stroke="#FF9800" strokeWidth="1.5"/>
                    <circle cx="32" cy="12" r="2" fill="#FF9800"/>
                    <rect x="22" y="24" width="20" height="2.5" rx="1.25" fill="#FF9800"/>
                    <rect x="22" y="30" width="20" height="2.5" rx="1.25" fill="#FFA726"/>
                    <rect x="22" y="36" width="15" height="2.5" rx="1.25" fill="#FFB74D"/>
                    <circle cx="22" cy="42" r="1.5" fill="#FF9800"/>
                    <circle cx="22" cy="47" r="1.5" fill="#FFA726"/>
                    <rect x="16" y="12" width="32" height="42" rx="3" fill="none" stroke="rgba(255,152,0,0.3)" strokeWidth="2"/>
                  </svg>
                </div>
                <div className="stat-info">
                  <h3>{stats.activeTasks}</h3>
                  <p>Active Tasks</p>
                </div>
              </div>

              <div className="stat-card-enhanced stat-completed">
                <div className="stat-icon">
                  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="16" y="12" width="32" height="42" rx="3" fill="white"/>
                    <rect x="24" y="8" width="16" height="8" rx="2" fill="white" stroke="#9C27B0" strokeWidth="1.5"/>
                    <circle cx="32" cy="12" r="2" fill="#9C27B0"/>
                    <path d="M22 26L26 30L34 22" stroke="#9C27B0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    <path d="M22 35L26 39L34 31" stroke="#B24DD1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    <path d="M22 44L26 48L34 40" stroke="#C77DFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    <rect x="16" y="12" width="32" height="42" rx="3" fill="none" stroke="rgba(156,39,176,0.3)" strokeWidth="2"/>
                  </svg>
                </div>
                <div className="stat-info">
                  <h3>{stats.completedTasks}</h3>
                  <p>Completed Tasks</p>
                </div>
              </div>

              {stats.overdueTasks > 0 && (
                <div className="stat-card-enhanced stat-danger">
                  <div className="stat-icon">
                    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M32 10L10 54H54L32 10Z" fill="white"/>
                      <path d="M32 16L14 52H50L32 16Z" fill="rgba(239,68,68,0.1)"/>
                      <rect x="30" y="26" width="4" height="14" rx="2" fill="#EF4444"/>
                      <circle cx="32" cy="46" r="2.5" fill="#EF4444"/>
                      <path d="M32 10L10 54H54L32 10Z" fill="none" stroke="rgba(239,68,68,0.3)" strokeWidth="2"/>
                    </svg>
                  </div>
                  <div className="stat-info">
                    <h3>{stats.overdueTasks}</h3>
                    <p>Overdue Tasks</p>
                  </div>
                </div>
              )}
            </div>

            {/* Recent Tasks Section */}
            <div className="dashboard-section">
              <div className="section-header">
                <h2>Recent Tasks</h2>
                <Link to="/tasks" className="view-all-link">View All →</Link>
              </div>
              
              {recentTasks.length > 0 ? (
                <div className="recent-tasks-grid">
                  {recentTasks.map(task => (
                    <Link to="/tasks" key={task._id} className="task-card-mini">
                      <div className="task-card-mini-header">
                        <span className={`status-badge-mini status-${task.status}`}>
                          {task.status.replace('_', ' ')}
                        </span>
                        {!task.is_viewed && user.role === 'member' && (
                          <span className="new-badge-mini">NEW</span>
                        )}
                        {isOverdue(task.due_date) && task.status !== 'completed' && (
                          <span className="overdue-badge-mini">OVERDUE</span>
                        )}
                      </div>
                      <h4>{task.task_description}</h4>
                      <div className="task-card-mini-footer">
                        <span className="task-group-mini">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{verticalAlign: 'middle', marginRight: '4px'}}>
                            <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/>
                          </svg>
                          {task.meetingTitle}
                        </span>
                        {task.due_date && (
                          <span className="task-due-mini">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{verticalAlign: 'middle', marginRight: '4px'}}>
                              <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                            </svg>
                            {formatDate(task.due_date)}
                          </span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No active tasks</p>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="dashboard-section">
              <h2>Quick Actions</h2>
              <div className="action-cards-grid">
                <Link to="/publications" className="action-card">
                  <div className="action-card-icon">
                    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M16 8L14 10V54L16 56H48L50 54V20L40 10H16Z" fill="white"/>
                      <path d="M40 10V18L42 20H50L40 10Z" fill="rgba(255,255,255,0.7)"/>
                      <rect x="22" y="28" width="20" height="3" rx="1.5" fill="#8B7AFF"/>
                      <rect x="22" y="34" width="20" height="3" rx="1.5" fill="#9B8AFF"/>
                      <rect x="22" y="40" width="14" height="3" rx="1.5" fill="#AB9AFF"/>
                      <path d="M16 8L14 10V54L16 56H48L50 54V20L40 10H16Z" fill="none" stroke="rgba(124,93,250,0.3)" strokeWidth="2"/>
                    </svg>
                  </div>
                  <h3>Publications</h3>
                  <p>Manage research publications</p>
                </Link>
                <Link to="/scholarships" className="action-card">
                  <div className="action-card-icon">
                    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M32 12L10 22V28L32 38L54 28V22L32 12Z" fill="white" opacity="0.95"/>
                      <path d="M10 32V38L32 48L54 38V32L32 42L10 32Z" fill="white" opacity="0.85"/>
                      <path d="M10 42V48L32 58L54 48V42L32 52L10 42Z" fill="white" opacity="0.75"/>
                      <circle cx="32" cy="18" r="3" fill="#4CAF50"/>
                      <path d="M32 12L10 22L32 32L54 22L32 12Z" fill="none" stroke="#4CAF50" strokeWidth="1.5"/>
                    </svg>
                  </div>
                  <h3>Scholarships</h3>
                  <p>Track scholarship information</p>
                </Link>
                <Link to="/congresses" className="action-card">
                  <div className="action-card-icon">
                    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="10" y="24" width="44" height="32" rx="2" fill="white"/>
                      <rect x="14" y="28" width="6" height="6" rx="1" fill="#00BCD4"/>
                      <rect x="22" y="28" width="6" height="6" rx="1" fill="#00BCD4"/>
                      <rect x="30" y="28" width="6" height="6" rx="1" fill="#00BCD4"/>
                      <rect x="38" y="28" width="6" height="6" rx="1" fill="#00BCD4"/>
                      <rect x="14" y="36" width="6" height="6" rx="1" fill="#26C6DA"/>
                      <rect x="22" y="36" width="6" height="6" rx="1" fill="#26C6DA"/>
                      <rect x="30" y="36" width="6" height="6" rx="1" fill="#26C6DA"/>
                      <rect x="38" y="36" width="6" height="6" rx="1" fill="#26C6DA"/>
                      <rect x="14" y="44" width="6" height="6" rx="1" fill="#4DD0E1"/>
                      <rect x="22" y="44" width="6" height="6" rx="1" fill="#4DD0E1"/>
                      <rect x="30" y="44" width="6" height="6" rx="1" fill="#4DD0E1"/>
                      <rect x="38" y="44" width="6" height="6" rx="1" fill="#4DD0E1"/>
                      <path d="M20 24L32 8L44 24H20Z" fill="white"/>
                      <path d="M20 24L32 10L44 24" stroke="#00BCD4" strokeWidth="2" fill="none"/>
                    </svg>
                  </div>
                  <h3>Congresses</h3>
                  <p>Record congress attendance</p>
                </Link>
                <Link to="/tasks" className="action-card">
                  <div className="action-card-icon">
                    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="14" y="10" width="36" height="46" rx="3" fill="white"/>
                      <rect x="22" y="6" width="20" height="8" rx="2" fill="white" stroke="#FF9800" strokeWidth="1.5"/>
                      <circle cx="32" cy="10" r="2.5" fill="#FF9800"/>
                      <rect x="20" y="22" width="24" height="3" rx="1.5" fill="#FF9800"/>
                      <rect x="20" y="28" width="24" height="3" rx="1.5" fill="#FFA726"/>
                      <rect x="20" y="34" width="18" height="3" rx="1.5" fill="#FFB74D"/>
                      <circle cx="20" cy="40" r="2" fill="#FF9800"/>
                      <circle cx="20" cy="46" r="2" fill="#FFA726"/>
                      <rect x="14" y="10" width="36" height="46" rx="3" fill="none" stroke="rgba(255,152,0,0.3)" strokeWidth="2"/>
                    </svg>
                  </div>
                  <h3>Tasks</h3>
                  <p>{user.role === 'director' ? 'Manage team tasks' : 'View your assignments'}</p>
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
